import { createSlice, PayloadAction } from "@reduxjs/toolkit"

interface VersionInfo {
  version: string
  hash: string
  coreHash: string
}

interface VersionState {
  current: VersionInfo | null
  latest: VersionInfo | null
  hasNewVersion: boolean
  hasCoreChange: boolean
  dismissed: boolean
  /** How many times the user has dismissed the toast for the current version */
  dismissCount: number
}

const initialState: VersionState = {
  current: null,
  latest: null,
  hasNewVersion: false,
  hasCoreChange: false,
  dismissed: false,
  dismissCount: 0,
}

/** Returns true when any hash differs between current and latest */
const detectNewVersion = (current: VersionInfo | null, latest: VersionInfo | null): boolean => {
  if (!current || !latest) return false
  return current.hash !== latest.hash || current.coreHash !== latest.coreHash
}

/** Returns true when the core submodule hash differs */
const detectCoreChange = (current: VersionInfo | null, latest: VersionInfo | null): boolean => {
  if (!current || !latest) return false
  return current.coreHash !== latest.coreHash
}

const versionSlice = createSlice({
  name: "version",
  initialState,
  reducers: {
    setCurrentVersion: (state, action: PayloadAction<VersionInfo>) => {
      state.current = action.payload
      state.hasNewVersion = detectNewVersion(state.current, state.latest)
      state.hasCoreChange = detectCoreChange(state.current, state.latest)
    },
    setLatestVersion: (state, action: PayloadAction<VersionInfo>) => {
      const previousLatest = state.latest
      state.latest = action.payload
      const wasNew = state.hasNewVersion
      const isNew = detectNewVersion(state.current, state.latest)

      // Check if this is a different version than what we were already tracking
      const isNewLatest =
        !previousLatest ||
        previousLatest.hash !== action.payload.hash ||
        previousLatest.coreHash !== action.payload.coreHash

      if (isNew && (!wasNew || isNewLatest)) {
        // Brand-new version detected — full reset
        state.dismissed = false
        state.dismissCount = 0
      } else if (isNew && state.dismissed) {
        // Still outdated on a subsequent poll — re-show the toast
        state.dismissed = false
      }

      state.hasNewVersion = isNew
      state.hasCoreChange = detectCoreChange(state.current, state.latest)
    },
    dismissUpdate: (state) => {
      state.dismissed = true
      state.dismissCount += 1
    },
    acknowledgeUpdate: (state) => {
      state.current = state.latest
      state.hasNewVersion = false
      state.hasCoreChange = false
      state.dismissed = false
      state.dismissCount = 0
    },
    resetVersionState: () => initialState,
  },
})

export const { setCurrentVersion, setLatestVersion, dismissUpdate, acknowledgeUpdate, resetVersionState } =
  versionSlice.actions
export default versionSlice.reducer
